import axios from 'axios'
import { defineStore } from 'pinia'
import type {
  ITicketCategoryCreateUpdatePayload,
  ITicketCategoryDetail,
  ITicketCategoryTreeNode,
  ITicketWorkGroupCreateUpdatePayload,
  ITicketWorkGroupDetail,
  ITicketWorkGroupItem,
} from '../types/settings/ITicketCategorySettings'
import { BASE_URL } from '@/environment/environment'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { onboardingApiUrl } from '@/modules/onboarding/utils/demoApi'

/**
 * Стор администрирования заявок (Ticket Settings Store).
 *
 * Управляет:
 * - Деревом категорий заявок (CRUD, поиск, валидация);
 * - Списком и таблицей групп исполнителей (CRUD, курсорная пагинация, поиск, сортировка);
 * - Состоянием загрузки и индикацией сохранения для форм админки.
 */
export const useTicketSettingsStore = defineStore('ticketSettingsStore', {
  state: () => ({
    /** Дерево категорий заявок */
    categoriesTree: [] as ITicketCategoryTreeNode[],
    /** Режим фильтра дерева: 'all' | 'custom' | 'expert' (expert = глобальные) */
    categoryTreeFilterMode: 'all' as string,
    /** Последние GET-параметры дерева (фильтр / поиск) */
    lastCategoryTreeParams: {} as Record<string, any>,
    /** Детальная информация о выбранной категории */
    currentCategoryDetail: null as ITicketCategoryDetail | null,
    /** Список групп исполнителей (для выпадающих списков) */
    workGroups: [] as ITicketWorkGroupItem[],
    /** Детальная информация о выбранной группе исполнителей */
    currentWorkGroupDetail: null as ITicketWorkGroupDetail | null,
    /** Курсор для следующей страницы групп исполнителей */
    workGroupsNextCursor: null as string | null,
    /** Поисковый запрос */
    searchQuery: '',

    // Для таблицы WorkGroupsTable
    /** Список групп для таблицы WorkGroupsTable */
    workGroupsList: [] as ITicketWorkGroupItem[],
    /** Общее количество групп исполнителей */
    totalWorkGroupsCount: 0,
    /** Курсор пагинации для таблицы */
    nextWorkGroupsCursor: null as string | null,
    /** Текущее поле/направление сортировки */
    currentWorkGroupsSortString: 'name' as string | null,
    /** Количество элементов на страницу */
    itemsPerPage: 25,
    /** Фильтры таблицы групп */
    workGroupsFilters: {
      search: '',
    },
  }),

  actions: {
    /**
     * Загружает данные для таблицы групп исполнителей (с поддержкой пагинации, поиска и сортировки)
     *
     * @param cursor Курсор для следующей страницы
     * @param limit Лимит элементов на страницу
     * @returns Список групп исполнителей
     */
    async fetchWorkGroupsTable(cursor?: string, limit?: number): Promise<ITicketWorkGroupItem[]> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, any> = {}

        if (cursor) {
          params.cursor = cursor
        }

        const pageSize = limit ?? this.itemsPerPage
        if (pageSize) {
          params.page_size = pageSize
        }

        if (this.workGroupsFilters.search?.trim()) {
          params.search = this.workGroupsFilters.search.trim()
        }

        if (this.currentWorkGroupsSortString) {
          params.sort = this.currentWorkGroupsSortString
        }

        const response = await axios.get<{ count?: number; next?: string | null; results?: ITicketWorkGroupItem[] } | ITicketWorkGroupItem[]>(
          onboardingApiUrl('ticket', '/ticket/work-group/'),
          { params },
        )

        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : []

        const nextCursor = !Array.isArray(response.data) ? (response.data?.next || null) : null
        this.nextWorkGroupsCursor = nextCursor

        if (cursor) {
          this.workGroupsList = [...this.workGroupsList, ...list]
        } else {
          this.workGroupsList = list
          this.totalWorkGroupsCount = !Array.isArray(response.data) ? (response.data?.count ?? list.length) : list.length
        }

        return this.workGroupsList
      } catch (error) {
        console.error('❌ Ошибка загрузки таблицы групп исполнителей:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить группы исполнителей'),
          time: 5000,
        })
        return []
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Сброс фильтров таблицы групп исполнителей к значениям по умолчанию
     */
    resetWorkGroupsFilters() {
      this.workGroupsFilters.search = ''
      this.currentWorkGroupsSortString = 'name'
    },

    /**
     * Собирает GET-параметры дерева категорий по режиму фильтра и поиску.
     * custom → is_daomed_main=false, expert → is_daomed_main=true (бэк: filterset_fields).
     */
    buildCategoryTreeParams(filterMode?: string, search?: string): Record<string, any> {
      const mode = filterMode ?? this.categoryTreeFilterMode
      const params: Record<string, any> = {}
      if (mode === 'custom')
        params.is_daomed_main = false
      else if (mode === 'expert')
        params.is_daomed_main = true
      const query = (search ?? '').trim()
      if (query)
        params.search = query
      return params
    },

    /**
     * Меняет режим фильтра дерева и перезагружает категории.
     */
    async setCategoryTreeFilter(mode: string, search?: string): Promise<ITicketCategoryTreeNode[]> {
      this.categoryTreeFilterMode = mode
      return this.fetchCategoriesTree(this.buildCategoryTreeParams(mode, search))
    },

    /**
     * Перезагружает дерево с текущим фильтром и опциональным поиском.
     */
    async reloadCategoryTree(search?: string): Promise<ITicketCategoryTreeNode[]> {
      return this.fetchCategoriesTree(this.buildCategoryTreeParams(this.categoryTreeFilterMode, search))
    },

    /**
     * Загружает дерево категорий заявок.
     * Параметры: search, is_daomed_main (фильтр Все/Свои/Глобальные).
     * Без аргумента — повторный запрос с последними params.
     */
    async fetchCategoriesTree(params?: Record<string, any>): Promise<ITicketCategoryTreeNode[]> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        if (params !== undefined)
          this.lastCategoryTreeParams = { ...params }

        const requestParams: Record<string, any> = { ...this.lastCategoryTreeParams }
        Object.keys(requestParams).forEach(key => {
          if (requestParams[key] === undefined || requestParams[key] === null || requestParams[key] === '')
            delete requestParams[key]
        })

        const response = await axios.get<{ results?: ITicketCategoryTreeNode[] } | ITicketCategoryTreeNode[]>(
          onboardingApiUrl('ticket', '/ticket/category/'),
          { params: requestParams },
        )

        const tree = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : []

        this.categoriesTree = tree
        return tree
      } catch (error) {
        console.error('❌ Ошибка загрузки категорий заявок:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить структуру категорий заявок'),
          time: 5000,
        })
        this.categoriesTree = []
        return []
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает детальную информацию о категории по ID
     */
    async fetchCategoryDetail(id: string): Promise<ITicketCategoryDetail | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<ITicketCategoryDetail>(
          `${BASE_URL}/api/ticket/category/${id}/`,
        )

        this.currentCategoryDetail = response.data
        return response.data
      } catch (error) {
        console.error(`❌ Ошибка загрузки деталей категории ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить данные категории'),
          time: 5000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Создает новую категорию заявки
     */
    async createCategory(payload: ITicketCategoryCreateUpdatePayload): Promise<ITicketCategoryDetail | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<ITicketCategoryDetail>(
          `${BASE_URL}/api/ticket/category/`,
          payload,
        )

        feedbackStore.showToast({
          type: 'success',
          message: 'Категория успешно создана',
          time: 3000,
        })

        await this.fetchCategoriesTree()
        return response.data
      } catch (error) {
        console.error('❌ Ошибка создания категории:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать категорию'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обновляет существующую категорию заявки
     */
    async updateCategory(
      id: string,
      payload: Partial<ITicketCategoryCreateUpdatePayload>,
    ): Promise<ITicketCategoryDetail | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch<ITicketCategoryDetail>(
          `${BASE_URL}/api/ticket/category/${id}/`,
          payload,
        )

        feedbackStore.showToast({
          type: 'success',
          message: 'Категория успешно обновлена',
          time: 3000,
        })

        await this.fetchCategoriesTree()
        return response.data
      } catch (error) {
        console.error(`❌ Ошибка обновления категории ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось сохранить изменения категории'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Переключает активность категории (PATCH is_active) без глобального лоадера.
     */
    async toggleCategoryActive(id: string, isActive: boolean): Promise<boolean> {
      const findAndSet = (
        nodes: ITicketCategoryTreeNode[],
        value: boolean,
      ): ITicketCategoryTreeNode | null => {
        for (const node of nodes) {
          if (String(node.id) === String(id)) {
            node.is_active = value
            return node
          }
          if (node.children?.length) {
            const found = findAndSet(node.children, value)
            if (found) return found
          }
        }
        return null
      }

      const previous = (() => {
        const walk = (nodes: ITicketCategoryTreeNode[]): boolean | undefined => {
          for (const node of nodes) {
            if (String(node.id) === String(id)) return node.is_active
            if (node.children?.length) {
              const nested = walk(node.children)
              if (nested !== undefined) return nested
            }
          }
          return undefined
        }
        return walk(this.categoriesTree)
      })()

      findAndSet(this.categoriesTree, isActive)

      try {
        await axios.patch(`${BASE_URL}/api/ticket/category/${id}/`, { is_active: isActive })
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'success',
          message: isActive ? 'Категория активирована' : 'Категория деактивирована',
          time: 3000,
        })
        return true
      } catch (error) {
        if (previous !== undefined) findAndSet(this.categoriesTree, previous)
        console.error('❌ Ошибка переключения активности категории:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить активность категории'),
          time: 3000,
        })
        return false
      }
    },

    /**
     * Удаляет категорию заявки
     */
    async deleteCategory(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.delete(`${BASE_URL}/api/ticket/category/${id}/`)

        feedbackStore.showToast({
          type: 'success',
          message: 'Категория успешно удалена',
          time: 3000,
        })

        await this.fetchCategoriesTree()
        return true
      } catch (error) {
        console.error(`❌ Ошибка удаления категории ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить категорию'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает список групп исполнителей заявок с поддержкой серверного поиска и пагинации
     */
    async fetchWorkGroups(search?: string, cursor?: string | null): Promise<ITicketWorkGroupItem[]> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, any> = {}
        if (search) {
          params.search = search
        }
        if (cursor) {
          params.cursor = cursor
        }

        const response = await axios.get<{ count?: number; next?: string | null; results?: ITicketWorkGroupItem[] } | ITicketWorkGroupItem[]>(
          onboardingApiUrl('ticket', '/ticket/work-group/'),
          { params },
        )

        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : []

        const nextCursor = !Array.isArray(response.data) ? (response.data?.next || null) : null
        this.workGroupsNextCursor = nextCursor

        if (cursor) {
          this.workGroups = [...this.workGroups, ...list]
        } else {
          this.workGroups = list
        }

        return this.workGroups
      } catch (error) {
        console.error('❌ Ошибка загрузки групп исполнителей:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить группы исполнителей'),
          time: 5000,
        })
        return []
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает детальную информацию о группе исполнителей по ID
     */
    async fetchWorkGroupDetail(id: number | string): Promise<ITicketWorkGroupDetail | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<ITicketWorkGroupDetail>(
          `${BASE_URL}/api/ticket/work-group/${id}/`,
        )

        this.currentWorkGroupDetail = response.data
        return response.data
      } catch (error) {
        console.error(`❌ Ошибка загрузки деталей группы исполнителей ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить данные группы исполнителей'),
          time: 5000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Создает новую группу исполнителей
     */
    async createWorkGroup(payload: ITicketWorkGroupCreateUpdatePayload): Promise<ITicketWorkGroupDetail | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<ITicketWorkGroupDetail>(
          `${BASE_URL}/api/ticket/work-group/`,
          payload,
        )

        feedbackStore.showToast({
          type: 'success',
          message: 'Группа исполнителей успешно создана',
          time: 3000,
        })

        await this.fetchWorkGroups()
        await this.fetchWorkGroupsTable()
        return response.data
      } catch (error) {
        console.error('❌ Ошибка создания группы исполнителей:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать группу исполнителей'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обновляет существующую группу исполнителей
     */
    async updateWorkGroup(
      id: number | string,
      payload: Partial<ITicketWorkGroupCreateUpdatePayload>,
    ): Promise<ITicketWorkGroupDetail | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch<ITicketWorkGroupDetail>(
          `${BASE_URL}/api/ticket/work-group/${id}/`,
          payload,
        )

        feedbackStore.showToast({
          type: 'success',
          message: 'Группа исполнителей успешно обновлена',
          time: 3000,
        })

        await this.fetchWorkGroups()
        await this.fetchWorkGroupsTable()
        return response.data
      } catch (error) {
        console.error(`❌ Ошибка обновления группы исполнителей ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить группу исполнителей'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Удаляет группу исполнителей
     */
    async deleteWorkGroup(id: number | string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.delete(`${BASE_URL}/api/ticket/work-group/${id}/`)

        feedbackStore.showToast({
          type: 'success',
          message: 'Группа исполнителей успешно удалена',
          time: 3000,
        })

        await this.fetchWorkGroups()
        await this.fetchWorkGroupsTable()
        return true
      } catch (error) {
        console.error(`❌ Ошибка удаления группы исполнителей ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить группу исполнителей'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
  },
})
